// react
import React, { ReactNode } from "react";

import { faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar, Text } from "@performyard/py-components";

//components
import LoadingEmployeeCard from "./LoadingEmployeeCard";

//api
import { useCompanyFields, useCurrentUser, useIsUserInChainOfCommand } from "../../api/hooks";

// utils
import { areFieldsVisible } from "./utils";

import Button from "../Button";
// styles
import css from "./EmployeeCard.module.scss";

type EmployeeCardProps = {
	employeeData: {
		id: string;
		name?: string;
		jobTitle?: string;
		email?: string;
		isActive?: boolean;
	};
	isLoading?: boolean;
	isDashboardCard?: boolean;
	isDetailsPage?: boolean;
	maxWidth?: string | null;
	onAvatarButtonClick?: (id: string) => void;
	avatarButtonLabel?: string | ReactNode;
	className?: string;
	avatarUrl?: string;
};

const EmployeeCard = ({
	employeeData,
	isLoading = false,
	isDashboardCard = false,
	isDetailsPage = false,
	maxWidth = undefined,
	onAvatarButtonClick = () => {},
	avatarButtonLabel = "",
	className,
	avatarUrl,
}: EmployeeCardProps) => {
	const { data: currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
	const userID = currentUser?.me?.authorization?.payload?.user_id;
	const userRole = currentUser?.me?.authorization?.payload?.user_primary_role;
	const companyID = currentUser?.me?.authorization?.payload?.company_id;

	const userIsAdmin = userRole === "admin";
	const userIsBasicAdmin = userRole === "basic-admin";
	const userIsBillingAdmin = userRole === "billing-admin";

	const userPermissions = ["all"];
	const fieldsToCheckVisibility = [
		{ name: "email", lookup: "Email" },
		{ name: "jobTitle", lookup: "Job Title" },
	];

	const { data: companyFields, isLoading: isCompanyFieldsLoading } = useCompanyFields(companyID || "");

	const { data: userIsAbove, isLoading: isUserIsAboveLoading } = useIsUserInChainOfCommand(
		employeeData?.id,
		!employeeData?.id
	);

	// this is an object that contains key/value pairs of the field permissions that the user can view
	let canViewFields: any = {
		email: false,
		jobTitle: false,
	};

	if (employeeData.id) {
		if (userID === employeeData.id || employeeData.id === "") {
			userPermissions.push("self");
		}
		if (userIsAdmin || userIsBasicAdmin || userIsBillingAdmin) {
			userPermissions.push("admin");
		}
	}

	if (userIsAbove && userIsAbove.isManager) {
		userPermissions.push("manager");
	}

	if (companyFields && userPermissions.length) {
		canViewFields = areFieldsVisible(fieldsToCheckVisibility, companyFields, userPermissions);
	}

	const classname = className ? className : !isDashboardCard ? css.notDashboardCard : undefined;

	const displayEmail = (isDetailsPage || isDashboardCard) && canViewFields.email;
	const isComponentLoading = isLoading || isCompanyFieldsLoading || isUserIsAboveLoading || isCurrentUserLoading;

	return (
		<div className={css.cardContainer} style={{ maxWidth: maxWidth || undefined }}>
			{!isComponentLoading ? (
				<div className={`flex flex-col items-center justify-center gap-2 p-4 pb-8 ${classname}`}>
					<div data-testid="main-avatar" className={css.employeeAvatarContainer}>
						{avatarButtonLabel ? (
							<button
								onClick={() => onAvatarButtonClick(employeeData.id)}
								aria-label={typeof avatarButtonLabel === "string" ? avatarButtonLabel : undefined}
								className="cursor-pointer border-0 bg-transparent p-0"
							>
								<Avatar
									size={120}
									name={employeeData.name}
									dangerouslySetClassName={css.employeeAvatar}
									id={employeeData.id}
									url={avatarUrl}
								/>
							</button>
						) : (
							<Avatar
								size={120}
								name={employeeData.name}
								dangerouslySetClassName={css.employeeAvatar}
								id={employeeData.id}
								url={avatarUrl}
							/>
						)}
					</div>

					<div className={css.textContent}>
						<Text
							data-testid="employee-name"
							dangerouslySetClassName={`${css.employeeName} w-full break-words`}
							size="paragraph-1"
							weight="semi-bold"
							overrideElement="h1"
							wordBreak={true}
						>
							{employeeData.name}
						</Text>
						{canViewFields.jobTitle && employeeData.jobTitle ? (
							<Text
								data-testid="secondary-data"
								dangerouslySetClassName={css.secondaryData}
								size="paragraph-3"
								weight="semi-bold"
							>
								{employeeData.jobTitle}
							</Text>
						) : null}
						{displayEmail ? (
							<div className={css.employeeEmail}>
								<FontAwesomeIcon icon={faEnvelope} className={css.emailIcon} />
								<a data-testid="employee-email" href={`mailto:${employeeData.email}`}>
									{employeeData.email}
								</a>
							</div>
						) : null}
					</div>

					{(isDashboardCard || isDashboardCard) && (
						<div
							className={`${isDashboardCard ? css.buttonContainer : ""} ${
								isDetailsPage ? css.editContainer : ""
							}`}
						>
							{isDashboardCard ? (
								<Button isHollow={true} href={`/details/${employeeData.id}/`}>
									View Details
								</Button>
							) : null}
						</div>
					)}
				</div>
			) : (
				<LoadingEmployeeCard className={className} />
			)}
		</div>
	);
};

export default EmployeeCard;
